import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { Template, TemplateSearchParams } from '../../interfaces/template.interface';
import { TemplateActions } from '../actions/template.actions';

export interface TemplateState extends EntityState<Template> {
  error: unknown;
  pending: boolean;
  search: {
    params: TemplateSearchParams;
    results: string[];
  };
}

const adapter: EntityAdapter<Template> = createEntityAdapter<Template>({
  selectId: (template: Template): string => template.id,
});

export const initialState: TemplateState = adapter.getInitialState({
  error: null,
  pending: false,
  search: {
    params: {},
    results: [],
  },
});

const TemplateReducer = createReducer(
  initialState,
  on(TemplateActions.clearState, (): TemplateState => initialState),
  on(TemplateActions.get, (state): TemplateState => ({ ...state, pending: true })),
  on(
    TemplateActions.getSuccess,
    (state, { template }): TemplateState =>
      adapter.upsertOne(template, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    TemplateActions.getFailure,
    (state, { error }): TemplateState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    TemplateActions.search,
    (state, { params }): TemplateState => ({
      ...state,
      pending: true,
      search: { params, results: [] },
    }),
  ),
  on(
    TemplateActions.searchSuccess,
    (state, { templates }): TemplateState =>
      adapter.upsertMany(templates, {
        ...state,
        search: {
          params: state.search.params,
          results: templates.map(({ id }) => id),
        },
        error: null,
        pending: false,
      }),
  ),
  on(
    TemplateActions.searchFailure,
    (state, { error }): TemplateState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    TemplateActions.create,
    (state): TemplateState => ({
      ...state,
      error: null,
      pending: true,
    }),
  ),
  on(
    TemplateActions.createSuccess,
    (state, { payload }): TemplateState =>
      adapter.upsertOne(payload, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    TemplateActions.createFailure,
    (state, { error }): TemplateState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    TemplateActions.update,
    (state): TemplateState => ({
      ...state,
      error: null,
      pending: true,
    }),
  ),
  on(
    TemplateActions.updateSuccess,
    (state, { payload }): TemplateState =>
      adapter.upsertOne(payload, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    TemplateActions.updateFailure,
    (state, { error }): TemplateState => ({
      ...state,
      error,
      pending: false,
    }),
  ),

  on(
    TemplateActions.delete,
    (state): TemplateState => ({
      ...state,
      error: null,
      pending: true,
    }),
  ),
  on(
    TemplateActions.deleteSuccess,
    (state, { id }): TemplateState =>
      adapter.removeOne(id, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    TemplateActions.deleteFailure,
    (state, { error }): TemplateState => ({
      ...state,
      error,
      pending: false,
    }),
  ),

  on(
    TemplateActions.duplicate,
    (state): TemplateState => ({
      ...state,
      error: null,
      pending: true,
    }),
  ),
  on(
    TemplateActions.duplicateSuccess,
    (state, { payload }): TemplateState =>
      adapter.upsertOne(payload, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    TemplateActions.duplicateFailure,
    (state, { error }): TemplateState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    TemplateActions.resetError,
    (state): TemplateState => ({
      ...state,
      error: null,
      pending: false,
    }),
  ),
);

const { selectAll } = adapter.getSelectors();

export const selectAllTemplate = selectAll;

export const reducer = (state: TemplateState | undefined, action: Action) =>
  TemplateReducer(state, action);
